import { useCallback } from 'react';
import { usePurchaseEvents } from '@hub/features/purchase/hooks/use-purchase-events';
import {
  clearCheckoutSource,
  getCheckoutSessionId,
  getCheckoutSource,
  getClientDevice,
  setCheckoutEntryPath,
  setCheckoutSessionId,
  setCheckoutSource,
} from '@monorepo/client-common';
import {
  BannerClickedProperties,
  CheckoutEntryPath,
  CheckoutSource,
  EventTypes,
  OptionalAssets,
} from '@monorepo/types';
import { useCart } from '../../../../../../api/queries';
import { useStore } from '../../../../../../helpers/use-store';

interface UseBannerTrackingParams {
  bannerName: string;
  bannerId?: string;
  link?: string;
  programId?: string;
  beforeNavigate?: () => void;
}

export const usePromotionalBannersState = ({
  bannerName,
  bannerId,
  link,
  programId: externalProgramId,
  beforeNavigate,
}: UseBannerTrackingParams) => {
  const { data: cart, isLoading: isCartLoading } = useCart();
  const {
    dataStore: { applicationConfigStore },
  } = useStore();
  const { handlePurchaseEvent } = usePurchaseEvents();

  const handleBannerClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      try {
        const targetElement =
          ((event.target as HTMLElement | null)?.closest(
            '[data-is-checkout],[data-banner-id]',
          ) as HTMLElement | null) ?? (event.currentTarget as HTMLElement);
        const isCheckout =
          targetElement.getAttribute('data-is-checkout') === 'true';

        const dataButtonId =
          targetElement.getAttribute('data-banner-id') ||
          bannerId ||
          bannerName;
        const destinationUrl =
          targetElement.getAttribute('href') ||
          targetElement.getAttribute('data-href') ||
          link ||
          '';

        if (!isCheckout) {
          // Not a checkout banner, just open the link if it exists
          if (destinationUrl) {
            beforeNavigate?.();
            window.open(destinationUrl, '_blank', 'noopener,noreferrer');
          }
          return;
        }

        if (isCartLoading || !cart) {
          return;
        }

        const bannerText =
          targetElement.getAttribute('alt') ||
          targetElement.getAttribute('title') ||
          targetElement.innerText?.trim() ||
          targetElement.textContent?.trim() ||
          bannerName;

        const urlParams = new URLSearchParams(window.location.search);
        const urlProgramId = urlParams.get('program_id');
        const programId = urlProgramId || externalProgramId || undefined;

        const assetKeys: OptionalAssets[] = [
          OptionalAssets.Forex,
          OptionalAssets.Futures,
          OptionalAssets.DayTrade,
          OptionalAssets.Swing,
          OptionalAssets.QuickBuy,
        ];

        const hasCartItems = assetKeys.some((assetKey) => {
          const assetCart = cart[assetKey];
          return assetCart && assetCart.items && assetCart.items.length > 0;
        });

        let checkoutSessionId = getCheckoutSessionId();
        const hasActiveSession = !!checkoutSessionId;

        let checkoutEntryPath: CheckoutEntryPath;
        if (!hasCartItems && !hasActiveSession) {
          checkoutEntryPath = CheckoutEntryPath.NewOrder;
          checkoutSessionId = setCheckoutSessionId();
        } else {
          checkoutEntryPath = CheckoutEntryPath.ResumeImplicit;

          if (!checkoutSessionId) {
            console.warn(
              '[Banner Tracking] Cart exists but no checkout session ID found. Creating new session.',
              { hasCartItems, assetKeys },
            );
            checkoutSessionId = setCheckoutSessionId();
          }
        }

        setCheckoutEntryPath(checkoutEntryPath);

        const currentSource = getCheckoutSource();
        if (
          currentSource === CheckoutSource.CartWidget ||
          currentSource === CheckoutSource.OrdersPage
        ) {
          clearCheckoutSource();
        }

        setCheckoutSource(CheckoutSource.OverviewBanner);

        const device = getClientDevice() === 'mobile' ? 'mobile' : 'desktop';

        const brand =
          applicationConfigStore.applicationConfig?.name || 'unknown';

        const properties: BannerClickedProperties = {
          checkout_session_id: checkoutSessionId,
          checkout_entry_path: checkoutEntryPath,
          checkout_source: CheckoutSource.OverviewBanner,
          banner_id: dataButtonId,
          banner_text: bannerText,
          destination_url: destinationUrl,
          device,
          brand,
          ...(programId && { program_id: programId }),
        };

        handlePurchaseEvent(EventTypes.CHECKOUT_CLICKED_BANNER, properties);

        if (destinationUrl) {
          beforeNavigate?.();
          const url = new URL(destinationUrl, window.location.origin);
          url.searchParams.set('checkout_session_id', checkoutSessionId);
          url.searchParams.set('checkout_entry_path', checkoutEntryPath);
          url.searchParams.set(
            'checkout_source',
            CheckoutSource.OverviewBanner,
          );
          window.open(url.toString(), '_blank', 'noopener,noreferrer');
        }
      } catch (error) {
        console.error('[Banner Tracking] Error tracking banner click:', error);
      }
    },
    [
      isCartLoading,
      externalProgramId,
      bannerName,
      bannerId,
      link,
      applicationConfigStore,
      beforeNavigate,
    ],
  );

  return { handleBannerClick, isCartLoading };
};
